/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { Form, Grid } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';
import {
  CoordonneesContacts,
  TypeComponent,
} from '@mairistem/administres-utils';

import { useTypesVoies } from '@/hooks';
import {
  buildAdresseLine,
  composeLibelleVoie,
  estTypeAdresseCommune,
  findTypeVoie,
  paysTypeAdresse,
  stripTypeVoie,
  toNumeroVoie,
  toPhoneInputValue,
  toTypeAdresseCode,
  toTypeAdresseLabel,
} from '@/utils';

import PanelModalEdition from '../../PanelModalEdition';
import CommuneDelegueField from '../CommuneDelegueField';

interface InformationsPartenaireResidenceModalProps {
  partenaire: any;
  updated: boolean;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsPartenaireResidenceModal = (
  props: InformationsPartenaireResidenceModalProps,
) => {
  const { partenaire, updated, onSave, onChange, onClose, onCancel } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  // Pas de colonne type de voie pour la résidence : déduit du libellé pour
  // l'étiquette du champ Voie (« Impasse » devant « DES PIGNATEU »), comme
  // pour les adresses de la convention. Seulement en « Commune » (cf.
  // estTypeAdresseCommune).
  const typeAdresse = toTypeAdresseLabel(partenaire?.residenceType);
  const typesVoies = useTypesVoies();
  const typeVoie = React.useMemo(
    () =>
      estTypeAdresseCommune(typeAdresse)
        ? findTypeVoie(partenaire?.residenceLibelleVoie, typesVoies)
        : null,
    [typeAdresse, partenaire?.residenceLibelleVoie, typesVoies],
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        residenceLibelleCommuneDelegue: libelle,
        residenceCommuneDelegue: drapeau,
      });
    },
    [onChange],
  );

  const mapAdresseResidence = React.useCallback(
    (adresse: Record<string, unknown>) => {
      const numeroVoirie = _.isNil(adresse.numeroVoirie)
        ? toNumeroVoie(partenaire?.residenceNumeroVoirie)
        : adresse.numeroVoirie;
      // Type de voie (« Rue ») renvoyé à part par AdresseForm à la sélection
      // d'une voie du référentiel : sans colonne dédiée, il va dans le libellé.
      // Hors « Commune », la voie saisie contient déjà son type.
      const commune = estTypeAdresseCommune(adresse.type ?? typeAdresse);
      let voie = adresse.voie;
      if (_.isNil(adresse.voie)) {
        voie = partenaire?.residenceLibelleVoie;
      } else if (commune) {
        voie = composeLibelleVoie(adresse.typeVoie ?? typeVoie, adresse.voie);
      }

      return {
        ...(!_.isNil(adresse.type) && {
          residenceType: toTypeAdresseCode(adresse.type),
        }),
        ...(!_.isNil(adresse.codePostal) && {
          residenceCodePostal: adresse.codePostal,
        }),
        ...(!_.isNil(adresse.commune) && {
          residenceLocalite: adresse.commune,
        }),
        ...(!_.isNil(adresse.pays) && { residencePays: adresse.pays }),
        // Après le pays vidé par la réinitialisation d'AdresseForm (même lot).
        ...(paysTypeAdresse(adresse.type) && {
          residencePays: paysTypeAdresse(adresse.type),
        }),
        ...(!_.isNil(adresse.voie) && { residenceLibelleVoie: voie }),
        ...(!_.isNil(adresse.numeroVoirie) && {
          residenceNumeroVoirie: adresse.numeroVoirie,
        }),
        ...((!_.isNil(adresse.voie) || !_.isNil(adresse.numeroVoirie)) && {
          residenceAdresse: buildAdresseLine({ numeroVoirie, voie }),
        }),
      };
    },
    [
      partenaire?.residenceNumeroVoirie,
      partenaire?.residenceLibelleVoie,
      typeAdresse,
      typeVoie,
    ],
  );

  const handleItemChangeAdresseResidence = React.useCallback(
    ({ adresse }: { adresse: Record<string, unknown> }) => {
      onChange(mapAdresseResidence(adresse));
    },
    [mapAdresseResidence, onChange],
  );

  const handleChangeAdresseResidence = React.useCallback(
    ({ target }: { target: { name: string; value: unknown } }) => {
      const field = _.replace(target.name, 'adresse.', '');
      onChange(mapAdresseResidence({ [field]: target.value }));
    },
    [mapAdresseResidence, onChange],
  );

  const handleChangeContact = React.useCallback(
    (_e: React.ChangeEvent<HTMLInputElement>, data: any) => {
      const mapping: Record<string, string> = {
        tel1: 'telephone1',
        tel2: 'telephone2',
        mail: 'courriel',
      };
      const field = mapping[data?.name];
      if (field) {
        onChange({ [field]: data.value });
      }
    },
    [onChange],
  );

  const handleSave = React.useCallback(() => {
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="partenaire-residence-modal"
      errors={errors}
      loading={loading}
      header="ADRESSE DE RÉSIDENCE ET CONTACT"
      updated={updated}
      scrollingContent
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <AdresseForm
                noForm
                saisie="libre"
                handleTypes
                displayDepartement={false}
                adresse={{
                  type: typeAdresse,
                  codePostal: partenaire?.residenceCodePostal,
                  commune: partenaire?.residenceLocalite,
                  pays: partenaire?.residencePays,
                  // `?? ''` obligatoire : le champ Voie d'`AdresseForm` est
                  // rendu en recherche assistée (type « Commune ») et concatène
                  // sa valeur (`voie + lieuDit`), ce qui afficherait le texte
                  // « null » quand la voie n'est pas renseignée.
                  voie: stripTypeVoie(
                    partenaire?.residenceLibelleVoie,
                    typeVoie,
                  ),
                  typeVoie,
                  numeroVoirie: toNumeroVoie(partenaire?.residenceNumeroVoirie),
                }}
                onItemChange={handleItemChangeAdresseResidence}
                onChange={handleChangeAdresseResidence}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <CommuneDelegueField
                data-testid="partenaire-residence-libelle-commune-delegue"
                libelle={partenaire?.residenceLibelleCommuneDelegue}
                drapeau={partenaire?.residenceCommuneDelegue}
                onChange={handleChangeCommuneDelegue}
              />
            </Grid.Column>
          </Grid.Row>
          {/* CoordonneesContacts en mode inline ajoute déjà 1em au-dessus */}
          <Grid.Row style={{ paddingTop: 0 }}>
            <Grid.Column width={16}>
              <CoordonneesContacts
                inline
                typeComponent={TypeComponent.PACS}
                tel1={toPhoneInputValue(partenaire?.telephone1) ?? ''}
                tel2={toPhoneInputValue(partenaire?.telephone2) ?? ''}
                mail={partenaire?.courriel ?? ''}
                onChange={handleChangeContact}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsPartenaireResidenceModal;
